import { Button, Space, Tag, Tree, Typography, Upload, message } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useEffect, useState } from 'react';
import { apiClient, apiErrorMessage } from '../../../utils';
import { InboxOutlined } from '@ant-design/icons';

export function RestoreBackupPage(props: { onComplete: () => void }) {
  const [grids, setGrids] = useState<any>({});
  const [integrations, setIntegrations] = useState<any>({});
  const [roles, setRoles] = useState<any>({});
  const [treeData, setTreeData] = useState<any[]>();
  const [checkedKeys, setCheckedKeys] = useState<string[]>(['all']);
  const [step, setStep] = useState(0);
  const [isRestoring, setIsRestoring] = useState(false);
  const [backupJson, setBackupJson] = useState<any>();

  const { Dragger } = Upload;
  const { Text } = Typography;

  useEffect(() => {
    if (!backupJson) {
      return;
    }

    const initialCheckedKeys = ['all'];
    if (Object.keys(backupJson.grids)?.length) {
      setGrids(backupJson.grids || []);
      const checkedGrids = Object.keys(backupJson.grids).map((gridId: string) => 'grid_' + gridId);
      initialCheckedKeys.push('grids', ...checkedGrids);
    }
    if (Object.keys(backupJson.integrations)?.length) {
      setIntegrations(backupJson.integrations);
      const checkedIntegrations = Object.keys(backupJson.integrations).map((integrationId: any) => 'integration_' + integrationId);
      initialCheckedKeys.push('integrations', ...checkedIntegrations);
    }
    if (Object.keys(backupJson.roles)?.length) {
      setRoles(backupJson.roles);
      const checkedRoles = Object.keys(backupJson.roles).map((roleId: any) => 'role_' + roleId);
      initialCheckedKeys.push('roles', ...checkedRoles);
    }
    if (Object.keys(backupJson.systemSettings)?.length) {
      initialCheckedKeys.push('systemSettings');
    }
    setCheckedKeys(initialCheckedKeys);
  }, [backupJson]);

  useEffect(() => {
    const titleDivStyle: React.CSSProperties = {
      marginBottom: '0.5rem',
    };

    const descriptionStyle: React.CSSProperties = {
      fontSize: '0.8rem',
      lineHeight: '0.7rem',
    };

    const newTreeData: DataNode[] = [
      {
        title: <div style={titleDivStyle}>Tümü</div>,
        key: 'all',
        children: [
          {
            title: <div style={titleDivStyle}>Veri setleri</div>,
            key: 'grids',
            disabled: backupJson && !Object.keys(backupJson?.grids)?.length,
            children:
              Object.keys(grids).length > 0
                ? Object.values(grids).map((grid: any) => {
                    return {
                      title: (
                        <div style={titleDivStyle}>
                          <div>{grid.grid.name}</div>
                          <Text type="secondary" style={descriptionStyle}>
                            {grid.grid.description}
                          </Text>
                        </div>
                      ),
                      key: 'grid_' + grid.grid.id,
                    };
                  })
                : [
                    {
                      title: (
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '16px' }}>
                          Yedek dosyasında herhangi bir veri seti mevcut değil.
                        </div>
                      ),
                      key: 'noGrid',
                      disabled: true,
                      checkable: false,
                    },
                  ],
          },
          {
            title: <div style={titleDivStyle}>Entegrasyonlar</div>,
            key: 'integrations',
            disabled: backupJson && !Object.keys(backupJson?.integrations)?.length,
            children:
              Object.keys(integrations).length > 0
                ? Object.values(integrations).map((integration: any) => {
                    return {
                      title: (
                        <div style={titleDivStyle}>
                          <div>{integration.integration.name}</div>
                          <Text type="secondary" style={descriptionStyle}>
                            {integration.integration.description}
                          </Text>
                        </div>
                      ),
                      key: 'integration_' + integration.integration.id,
                    };
                  })
                : [
                    {
                      title: (
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '16px', marginBottom: '1rem' }}>
                          Yedek dosyasında herhangi bir entegrasyon mevcut değil.
                        </div>
                      ),
                      key: 'noIntegration',
                      disabled: true,
                      checkable: false,
                    },
                  ],
          },
          {
            title: <div style={titleDivStyle}>Roller</div>,
            key: 'roles',
            disabled: backupJson && !Object.keys(backupJson?.roles)?.length,
            children:
              Object.keys(roles).length > 0
                ? Object.values(roles).map((role: any) => {
                    return {
                      title: (
                        <div style={titleDivStyle}>
                          <div>{role.role.name}</div>
                          <Text type="secondary" style={descriptionStyle}>
                            {role.role.description}
                          </Text>
                        </div>
                      ),
                      key: 'role_' + role.role.id,
                    };
                  })
                : [
                    {
                      title: (
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '16px', marginBottom: '1rem' }}>
                          Yedek dosyasında herhangi bir rol mevcut değil.
                        </div>
                      ),
                      key: 'noRole',
                      disabled: true,
                      checkable: false,
                    },
                  ],
          },
          {
            title: 'Sistem Ayarları',
            key: 'systemSettings',
            disabled: backupJson && !Object.keys(backupJson?.systemSettings)?.length,
          },
        ],
      },
    ];
    setTreeData(newTreeData);
  }, [Text, backupJson, grids, integrations, roles]);

  const onCheck = (checkedKeys: any, info: any) => {
    setCheckedKeys(checkedKeys);
  };

  const renderCheckedItems = () => {
    const tagStyle: React.CSSProperties = { marginRight: '0.5rem', width: '7rem', textAlign: 'center' };

    const checkedItems: any[] = [];
    checkedKeys?.forEach((key: string) => {
      if (key.includes('grid_')) {
        const grid = grids[key.split('_')[1]];
        if (grid) {
          checkedItems.push(
            <span>
              <Tag color="green" style={tagStyle}>
                Veri Seti
              </Tag>
              {grid.grid.name}
            </span>,
          );
        }
      } else if (key.includes('integration_')) {
        const integration = integrations[key.split('_')[1]];
        if (integration) {
          checkedItems.push(
            <span>
              <Tag color="blue" style={tagStyle}>
                Entegrasyon
              </Tag>
              {integration.integration.name}
            </span>,
          );
        }
      } else if (key.includes('role_')) {
        const role = roles[key.split('_')[1]];
        if (role) {
          checkedItems.push(
            <span>
              <Tag color="yellow" style={tagStyle}>
                Rol
              </Tag>
              {role.role.name}
            </span>,
          );
        }
      } else if (key === 'systemSettings') {
        checkedItems.push(
          <span>
            <Tag color="purple" style={tagStyle}>
              Sistem ayarları
            </Tag>
            Sistem ayarları
          </span>,
        );
      }
    });
    return checkedItems;
  };

  const filterItems = () => {
    let grids: object = {};
    let integrations: object = {};
    let roles: object = {};
    let systemSettings: object[] = [];

    if (checkedKeys.includes('all')) {
      grids = backupJson.grids;
      integrations = backupJson.integrations;
      roles = backupJson.roles;
      systemSettings = backupJson.systemSettings;
    } else {
      for (let key of checkedKeys) {
        if (key.includes('grid_')) {
          let gridIdentifier = Object.keys(backupJson.grids)?.find(g => backupJson.grids[g]?.grid.id === key.split('_')[1]) as string;
          const grid = backupJson?.grids[gridIdentifier];
          if (grid) {
            grids ? (grids = { ...grids, [gridIdentifier]: grid }) : (grids = { [gridIdentifier]: grid });
          }
        } else if (key.includes('integration_')) {
          const integrationIdentifier = Object.keys(backupJson.integrations)?.find(
            i => backupJson.integrations[i]?.integration.id === key.split('_')[1],
          ) as string;
          const integration = backupJson?.integrations[integrationIdentifier];
          if (integration) {
            integrations
              ? (integrations = { ...integrations, [integrationIdentifier]: integration })
              : (integrations = { [integrationIdentifier]: integration });
          }
        } else if (key.includes('role_')) {
          const roleIdentifier = Object.keys(backupJson.roles)?.find(r => backupJson.roles[r]?.role.id === key.split('_')[1]) as string;
          const role = backupJson?.roles[roleIdentifier];
          if (role) {
            roles ? (roles = { ...roles, [roleIdentifier]: role }) : (roles = { [roleIdentifier]: role });
          }
        } else if (key === 'systemSettings') {
          systemSettings = backupJson.systemSettings;
        }
      }
    }

    return {
      grids,
      integrations,
      roles,
      systemSettings,
    };
  };

  const restoreSelectedItems = () => {
    setIsRestoring(true);
    const reqObj = filterItems();

    apiClient
      .put('backups', reqObj)
      .then(response => {
        if (response.status === 200) {
          message.success('Yedek dosyası başarıyla geri yüklendi.');
          setBackupJson(null);
        } else {
          message.error('Yedek dosyası geri yüklenirken bir hata oluştu.');
          setBackupJson(null);
        }
      })
      .catch(reason => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        props.onComplete();
      });
  };

  const dummyUploadRequest = ({ onSuccess }: any) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  return (
    <div>
      {step === 0 && !backupJson && (
        <Dragger
          name="files"
          maxCount={1}
          customRequest={dummyUploadRequest}
          accept=".json"
          onChange={e => {
            const file = e.file?.originFileObj;
            if (file) {
              const reader = new FileReader();
              reader.onload = e => {
                const result = e.target?.result;
                if (result) {
                  setBackupJson(JSON.parse(result as string));
                }
              };
              reader.readAsText(file);
            }
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Bir dosya sürükleyin veya tıklayın</p>
          <p className="ant-upload-hint">Lütfen json uzantılı bir yedek dosyası yükleyin.</p>
        </Dragger>
      )}
      {step === 0 && backupJson && (
        <>
          <p>Yedek dosyasından geri yüklemek istediğiniz sistem öğelerini seçin.</p>
          <Tree
            switcherIcon={<></>}
            checkable
            defaultExpandAll
            checkedKeys={checkedKeys}
            onCheck={onCheck}
            treeData={treeData}
            selectable={false}
            expandedKeys={['all', 'systemSettings', 'grids', 'integrations', 'roles']}
          />
        </>
      )}
      {step === 1 && (
        <>
          <p>Aşağıdaki öğeler yedek dosyasından geri yüklenecek.</p>
          <Space direction="vertical">{renderCheckedItems()}</Space>
        </>
      )}
      <div style={{ marginTop: '2rem', textAlign: 'right' }}>
        <Space>
          <Button
            key="back"
            onClick={() => {
              if (step === 0) {
                props.onComplete();
                setBackupJson(null);
              } else {
                setStep(0);
              }
            }}
          >
            {step === 0 ? 'İptal' : 'Geri'}
          </Button>
          <Button
            key="submit"
            type="primary"
            disabled={!backupJson}
            loading={isRestoring}
            onClick={() => {
              if (step === 0) {
                setStep(1);
              } else {
                restoreSelectedItems();
              }
            }}
          >
            {step === 0 ? 'İlerle' : 'Geri yükle'}
          </Button>
        </Space>
      </div>
    </div>
  );
}
